import { defaultAnimation, MultilineInput } from '@amelio/ui-library';
import { useId, useState } from 'react';

import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';

export interface ReviewCommentsProps {
    /** Whether comments are enabled and the component should be displayed */
    commentsEnabled: boolean;
    /** Current comment value */
    value?: string;
    /** Callback fired when comment value changes */
    onCommentChange?: (value: string) => void;
    /** Callback fired when Enter is pressed (without Shift) - submits/goes to next question */
    onSubmit?: () => void;
    /** Callback fired when Enter is pressed (without Shift) - alternative to onSubmit */
    onNextQuestion?: () => void;
    /** Custom placeholder shown when the field is focused or has a value */
    placeholder?: string;
    /** Custom label/placeholder shown at rest */
    label?: string;
    /** Optional className for customization */
    className?: string;
    /** Whether to show the tooltip */
    showTooltip?: boolean;
    /** Custom tooltip text */
    tooltipText?: string;
    /** Id of what else describes the field, read with its label */
    ariaDescribedBy?: string;
    /** Test identifier for the component */
    dataTestId?: string;
}

export const ReviewComments = ({
    commentsEnabled,
    value,
    onCommentChange,
    onSubmit,
    onNextQuestion,
    placeholder,
    label,
    className = '',
    showTooltip = false,
    tooltipText,
    ariaDescribedBy,
    dataTestId = 'review-comments',
}: ReviewCommentsProps) => {
    const { t } = useAmelioTranslations();
    const isRtl = i18n.language.startsWith('ar');
    const [isFocused, setIsFocused] = useState(false);
    const labelId = useId();

    if (!commentsEnabled) {
        return null;
    }

    const restLabel = label ?? t('answer.addAnComment');
    const focusedPlaceholder = placeholder ?? t('answer.commentDetailsPlaceholder');
    const hasValue = Boolean(value && value.length > 0);
    const isLabelFloating = isFocused || hasValue;
    const currentPlaceholder = isLabelFloating ? focusedPlaceholder : restLabel;
    const handleEnterPress = onSubmit ?? onNextQuestion;

    return (
        <div
            className={`w-full ${className}`}
            data-testid={dataTestId}
        >
            <label
                id={labelId}
                className={`${defaultAnimation} block overflow-hidden text-base font-medium text-white sm:text-lg ${
                    isLabelFloating
                        ? 'mb-1 max-h-8 translate-y-0 opacity-100'
                        : 'pointer-events-none mb-0 max-h-0 -translate-y-2 opacity-0'
                }`}
                data-testid={`${dataTestId}-label`}
                aria-hidden={!isLabelFloating}
            >
                {restLabel}
            </label>

            <MultilineInput
                value={value ?? ''}
                onChange={(newValue) => onCommentChange?.(newValue)}
                onEnterPress={handleEnterPress}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                rows={1}
                autoGrow
                className={`focus:border-primary-500 w-full resize-none border-0 border-b border-white/30 bg-transparent px-3 py-2 text-base text-white placeholder:text-white/70 focus:outline-none sm:text-lg ${defaultAnimation}`}
                placeholder={currentPlaceholder}
                dir={isRtl ? 'rtl' : 'auto'}
                ariaLabel={restLabel}
                ariaDescribedBy={
                    [isLabelFloating && labelId, ariaDescribedBy].filter(Boolean).join(' ') ||
                    undefined
                }
                showTooltip={showTooltip}
                tooltipText={tooltipText ?? t('answer.addCommentOptional')}
                dataTestId={`${dataTestId}-input`}
            />
        </div>
    );
};
